import { KickPlayerButton } from '../components/RoomManagement';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { PageShell } from '../components/PageShell';
import { GlassPanel } from '../components/GlassPanel';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { MaterialIcon } from '../components/MaterialIcon';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { ConnectionStatusBadge } from '../components/ConnectionStatusBadge';
import { useGame } from '../state/gameContext';

export default function GameLobbyPage() {
  const { state, currentPlayer, toggleReady, startGame, runtimeMode, connectionStatus } = useGame();

  const [copyStatus, setCopyStatus] = useState('Copy invite link');
  const inviteLink = `${window.location.origin}/join?code=${encodeURIComponent(state.roomCode ?? '')}`;
  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopyStatus('Link copied');
    } catch {
      setCopyStatus('Copy the link above to invite friends');
    }
  };

  if (!state.roomCode) {
    return <Navigate to="/create" replace />;
  }

  const isHost = currentPlayer?.isHost ?? false;
  const connectedCount = state.players.filter((p) => p.isConnected).length;
  const canStart =
    state.canStartGame ?? (isHost && connectedCount >= 2);

  return (
    <PageShell mainClassName="relative z-10 pt-20 lg:ml-80 h-screen overflow-y-auto px-margin-mobile md:px-margin-desktop pb-64 md:pb-32 custom-scrollbar">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-stack-md mb-stack-lg pt-4">
        <div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h1 className="font-headline-md text-headline-md-mobile md:text-headline-md" data-testid="room-code">{state.roomCode}</h1>
            {runtimeMode === 'server' && <ConnectionStatusBadge status={connectionStatus} />}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-on-surface-variant font-label-sm">Invite link:</span>
            <code className="bg-surface-container break-all px-2 py-0.5 rounded text-primary text-label-sm">
              {inviteLink}
            </code>
            <button type="button" onClick={copyInvite} className="text-primary text-xs underline shrink-0" aria-live="polite">{copyStatus}</button>
          </div>
        </div>
        <GlassPanel className="flex-1 md:flex-none px-4 py-2 rounded-full border-primary/30 flex items-center gap-2">
          <MaterialIcon name="hourglass_empty" className="text-primary status-pulse" filled />
          <span className="font-label-bold text-primary">
            {runtimeMode === 'server' && connectionStatus !== 'connected'
              ? `Connecting (${connectionStatus})...`
              : isHost
                ? 'You are the host — start when ready'
                : 'Waiting for Host to start...'}
          </span>
        </GlassPanel>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-stack-lg">
        <section className="xl:col-span-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-stack-md">
            {state.players.map((player) => (
              <GlassPanel
                key={player.id}
                data-testid={`lobby-player-${player.id}`}
                className={`p-4 rounded-2xl flex flex-col items-center text-center relative ${
                  player.isHost ? 'neon-glow-primary border-primary/20' : ''
                }`}
              >
                {player.isHost && (
                  <div className="absolute top-3 right-3 bg-primary text-on-primary text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">
                    Host
                  </div>
                )}
                <PlayerAvatar
                  initials={player.initials}
                  gradient={player.gradient}
                  size="lg"
                  alt={`${player.name} avatar`}
                  ring={player.isHost ? 'border-4 border-primary p-1' : 'border-2 border-white/10 p-1'}
                  className="mb-3"
                />
                <h3 className="font-card-text text-on-surface mb-1">{player.name}</h3>
                <p className={`text-label-sm font-bold uppercase tracking-wider ${player.isReady ? 'text-secondary' : 'text-on-surface-variant'}`}>
                  {!player.isConnected ? 'Offline ? seat saved' : player.isReady ? 'Ready' : 'Connected'}
                </p>
                <KickPlayerButton playerId={player.id} />
              </GlassPanel>
            ))}
          </div>
        </section>

        <aside className="flex flex-col gap-stack-lg">
          <GlassPanel className="flex flex-col rounded-2xl h-[400px] border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-white/5">
              <h3 className="font-label-bold text-on-surface flex items-center gap-2">
                <MaterialIcon name="forum" className="text-primary text-[20px]" />
                Lobby Banter
              </h3>
              <span className="text-label-sm text-secondary">{connectedCount} Online</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-stack-sm custom-scrollbar">
              {state.log.map((msg) =>
                msg.system ? (
                  <div key={msg.id} className="text-center py-2">
                    <span className="text-[10px] text-on-surface-variant uppercase tracking-widest">{msg.text}</span>
                  </div>
                ) : (
                  <div key={msg.id} className={`flex flex-col ${msg.self ? 'self-end items-end' : ''}`}>
                    {msg.author && (
                      <span className={`text-[11px] font-bold mb-0.5 ${msg.self ? 'text-on-surface-variant' : 'text-primary'}`}>
                        {msg.author}
                      </span>
                    )}
                    <div
                      className={`p-2 rounded-lg inline-block max-w-[90%] border ${
                        msg.self ? 'bg-white/5 border-white/10 rounded-tr-none' : 'bg-primary/10 border-primary/20 rounded-tl-none'
                      }`}
                    >
                      <p className="text-sm">{msg.text}</p>
                    </div>
                  </div>
                ),
              )}
            </div>
          </GlassPanel>
          <GlassPanel className="p-4 rounded-2xl border-white/10">
            <h3 className="font-label-bold text-on-surface mb-4">Game Settings</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-on-surface-variant">Points to win</span><span className="font-bold">{state.settings.pointsToWin}</span></div>
              <div className="flex justify-between"><span className="text-on-surface-variant">Max players</span><span className="font-bold">{state.settings.maxPlayers}</span></div>
              <div className="flex justify-between"><span className="text-on-surface-variant">Wild cards</span><span className="font-bold text-primary">{state.settings.wildCardsEnabled ? 'On' : 'Off'}</span></div>
            </div>
          </GlassPanel>
        </aside>
      </div>

      <div className="fixed bottom-20 md:bottom-0 left-0 lg:left-80 right-0 p-margin-mobile md:p-stack-lg z-50">
        <GlassPanel className="max-w-4xl mx-auto p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 shadow-2xl border-primary/20">
          {currentPlayer && (
            <div className="flex items-center gap-4">
              <PlayerAvatar initials={currentPlayer.initials} gradient={currentPlayer.gradient} size="md" alt={`${currentPlayer.name} avatar`} />
              <div className="hidden sm:block">
                <p className="font-bold text-sm">{currentPlayer.name}</p>
                <p className={`text-[11px] font-bold uppercase ${currentPlayer.isReady ? 'text-secondary' : 'text-on-surface-variant'}`}>
                  {currentPlayer.isReady ? 'Ready' : 'Not ready'}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-stack-md w-full md:w-auto">
            <button
              type="button"
              onClick={toggleReady}
              data-testid="toggle-ready"
              className="flex-1 md:flex-none px-6 py-3 border-2 border-secondary text-secondary rounded-xl font-label-bold hover:bg-secondary/10 transition-all active:scale-95"
            >
              {currentPlayer?.isReady ? 'Unready' : 'Ready'}
            </button>
            <button
              type="button"
              onClick={startGame}
              disabled={!isHost || !canStart}
              data-testid="start-game"
              title={!isHost ? 'Only the host can start the game.' : !canStart ? 'Need at least two connected players.' : undefined}
              className="flex-1 md:flex-none px-8 py-3 bg-primary text-on-primary rounded-xl font-label-bold flex items-center justify-center gap-2 hover:brightness-110 transition-all active:scale-95 neon-glow-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100"
            >
              <MaterialIcon name="play_arrow" filled />
              Start Game
            </button>
          </div>
        </GlassPanel>
      </div>
      <MobileBottomNav active="/lobby" />
    </PageShell>
  );
}
